import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { DEFAULT_SELECTED_LIST, UNREAD_LIST } from '@/consts/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { handleError } from '@/utils/error'

import { listsService } from '../service/lists'

export const usePinnedLists = () => {
  const creatorId = useUnit($creatorId)
  const queryClient = useQueryClient()
  const temporaryPinnedListIdRef = useRef<string | null>(null)

  const pinList = useMutation(
    (listsToUpdate: { listId: string; pinned: boolean }[]) =>
      listsService.editPinnedListState({
        creatorId: creatorId!,
        listsToUpdate,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('all-lists-extended')
        queryClient.invalidateQueries('pinned-lists')
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while pinning list')
      },
    },
  )

  useEffect(
    () => {
      return () => {
        if (temporaryPinnedListIdRef.current) {
          pinList.mutateAsync([
            {
              listId: temporaryPinnedListIdRef.current,
              pinned: false,
            },
          ])
        }
      }
    },
    // it leads to infinite loop if pinList is included
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const onSelectList = useCallback(
    async (listId: string, isPinned: boolean) => {
      if (pinList.isLoading) {
        return
      }
      const listsToUpdate = []

      if (temporaryPinnedListIdRef.current) {
        listsToUpdate.push({
          listId: temporaryPinnedListIdRef.current,
          pinned: false,
        })
      }
      if (
        !isPinned &&
        ![UNREAD_LIST.id, DEFAULT_SELECTED_LIST.id].includes(listId)
      ) {
        listsToUpdate.push({ listId, pinned: true })
        temporaryPinnedListIdRef.current = listId
      }
      if (listsToUpdate.length) {
        await pinList.mutateAsync(listsToUpdate)
      }
    },
    [pinList],
  )

  return useMemo(
    () => ({
      onSelectList,
      isPinning: pinList.isLoading,
    }),
    [onSelectList, pinList],
  )
}
